// Role constants and utilities for the application
// Updated to match the new role structure

// Platform-level roles
export const PLATFORM_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  COMPANY_ADMIN: 'COMPANY_ADMIN'
};

// Business Unit-level roles
export const BU_ROLES = {
  ADMIN: 'Admin', // BU-level administrator
  SALES_REPRESENTATIVE: 'Sales Representative',
  SALES_MANAGER: 'Sales Manager',
  RECRUITER: 'Recruiter',
  RECRUITMENT_MANAGER: 'Recruitment Manager',
  HR: 'HR',
  HR_MANAGER: 'HR Manager',
  MANAGER: 'Manager',
  CONSULTANT: 'Consultant'
};

// All available roles for selection in forms
export const ROLE_OPTIONS = [
  BU_ROLES.SALES_REPRESENTATIVE,
  BU_ROLES.SALES_MANAGER,
  BU_ROLES.RECRUITER,
  BU_ROLES.RECRUITMENT_MANAGER,
  BU_ROLES.ADMIN,
  BU_ROLES.HR,
  BU_ROLES.HR_MANAGER,
  BU_ROLES.MANAGER,
];

// Company-level roles (for users not assigned to any BU)
export const COMPANY_ROLE_OPTIONS = [
  PLATFORM_ROLES.COMPANY_ADMIN
];

// Role hierarchy and permissions
export const ROLE_HIERARCHY = {
  [PLATFORM_ROLES.SUPER_ADMIN]: {
    level: 1,
    name: 'Super Admin',
    description: 'Platform-wide administrator with full access',
    canManage: ['companies', 'business_units', 'users']
  },
  [PLATFORM_ROLES.COMPANY_ADMIN]: {
    level: 2,
    name: 'Company Admin',
    description: 'Company-level administrator',
    canManage: ['business_units', 'users']
  },
  [BU_ROLES.ADMIN]: {
    level: 3,
    name: 'Admin',
    description: 'Business Unit-level administrator',
    canManage: ['users']
  },
  [BU_ROLES.HR_MANAGER]: {
    level: 4,
    name: 'HR Manager',
    description: 'Human Resources Manager',
    canManage: []
  },
  [BU_ROLES.RECRUITMENT_MANAGER]: {
    level: 4,
    name: 'Recruitment Manager',
    description: 'Recruitment Manager',
    canManage: []
  },
  [BU_ROLES.SALES_MANAGER]: {
    level: 4,
    name: 'Sales Manager',
    description: 'Sales Manager',
    canManage: []
  },
  [BU_ROLES.HR]: {
    level: 5,
    name: 'HR',
    description: 'Human Resources',
    canManage: []
  },
  [BU_ROLES.RECRUITER]: {
    level: 5,
    name: 'Recruiter',
    description: 'Recruiter',
    canManage: []
  },
  [BU_ROLES.SALES_REPRESENTATIVE]: {
    level: 5,
    name: 'Sales Representative',
    description: 'Sales Representative',
    canManage: []
  },
  [BU_ROLES.MANAGER]: {
    level: 5,
    name: 'Manager',
    description: 'Manager',
    canManage: []
  },
  [BU_ROLES.CONSULTANT]: {
    level: 6,
    name: 'Consultant',
    description: 'Restricted consultant portal user',
    canManage: []
  }
};

// Utility functions
export const isPlatformRole = (role) => {
  return Object.values(PLATFORM_ROLES).includes(role);
};

export const isBURole = (role) => {
  return Object.values(BU_ROLES).includes(role);
};

export const isAdminRole = (role) => {
  return role === PLATFORM_ROLES.SUPER_ADMIN || 
         role === PLATFORM_ROLES.COMPANY_ADMIN || 
         role === BU_ROLES.ADMIN;
};

export const isSuperAdmin = (role) => {
  return role === PLATFORM_ROLES.SUPER_ADMIN;
};

export const isCompanyAdmin = (role) => {
  return role === PLATFORM_ROLES.COMPANY_ADMIN;
};

export const isBUAdmin = (role) => {
  return role === BU_ROLES.ADMIN;
};

export const isSalesRole = (role) => {
  return role === BU_ROLES.SALES_REPRESENTATIVE || role === BU_ROLES.SALES_MANAGER;
};

export const isRecruiterRole = (role) => {
  return role === BU_ROLES.RECRUITER || role === BU_ROLES.RECRUITMENT_MANAGER;
};

export const isManagerRole = (role) => {
  return (
    role === BU_ROLES.MANAGER ||
    role === BU_ROLES.SALES_MANAGER ||
    role === BU_ROLES.RECRUITMENT_MANAGER ||
    role === BU_ROLES.HR_MANAGER
  );
};

export const isConsultantRole = (role) => {
  return role === BU_ROLES.CONSULTANT;
};

// Legacy role mapping for backward compatibility
export const LEGACY_ROLE_MAPPING = {
  'ADMIN': BU_ROLES.ADMIN, // Default to BU_ROLES.ADMIN for legacy ADMIN
  'USER': BU_ROLES.MANAGER // Default to MANAGER for legacy USER
};

// Convert legacy role to new role structure
export const convertLegacyRole = (role) => {
  if (!role) return null;
  const upperRole = role.toUpperCase();
  return LEGACY_ROLE_MAPPING[upperRole] || role;
};

// Convert legacy role with business unit context to determine actual role level
export const convertLegacyRoleWithContext = (role, hasBusinessUnits = false) => {
  if (!role) return null;
  const upperRole = role.toUpperCase();
  
  // If user has "ADMIN" role but no business units, they are a company admin
  if (upperRole === 'ADMIN' && !hasBusinessUnits) {
    return PLATFORM_ROLES.COMPANY_ADMIN;
  }
  
  // Otherwise, use the standard mapping
  return LEGACY_ROLE_MAPPING[upperRole] || role;
};

// Get the effective role for a user based on the correct hierarchy:
// 1. First check if user has a role in the currently selected business unit
// 2. If no BU role, fall back to the base user role
// 3. If no base role, return null
export const getEffectiveRole = (user, selectedBusinessUnit) => {
  // Priority 1: Role in currently selected business unit
  if (selectedBusinessUnit?.role) {
    return convertLegacyRole(selectedBusinessUnit.role);
  }
  
  // Priority 2: Base user role (fallback when no BU is selected)
  if (user?.role) {
    return convertLegacyRoleWithContext(user.role, user.business_units && user.business_units.length > 0);
  }
  
  // Priority 3: No role found
  return null;
};
